import {
  computePackageMetrics,
  computeServingMetrics,
  isFiniteNumber,
} from '@/lib/metrics';
import { matchesSearchBlob, searchBlob } from '@/lib/searchText';
import { selectPrice } from '@/lib/priceSelection';
import type {
  CatalogDataset,
  CatalogFilters,
  DrinkCategory,
  Nutrition,
  Offer,
  PriceOverride,
  Product,
  SortDirection,
  SortKey,
} from '@/types/data';

export interface CatalogRow {
  product: Product;
  nutrition: Nutrition | null;
  offers: Offer[];
  serving: ReturnType<typeof computeServingMetrics>;
  package: ReturnType<typeof computePackageMetrics>;
  price: ReturnType<typeof selectPrice>;
  alcoholPer100Calories: number | null;
  alcoholPerDollar: number | null;
  caloriesPerStandardDrink: number | null;
  dollarsPerStandardDrink: number | null;
  missingReason: string | null;
}

export function getNutritionMap(
  nutrition: Nutrition[],
): Map<string, Nutrition> {
  return new Map(nutrition.map((n) => [n.productId, n]));
}

export function getOffersByProduct(offers: Offer[]): Map<string, Offer[]> {
  const map = new Map<string, Offer[]>();
  for (const offer of offers) {
    const list = map.get(offer.productId) ?? [];
    list.push(offer);
    map.set(offer.productId, list);
  }
  return map;
}

export function buildCatalogRows(
  dataset: CatalogDataset,
  overrides: Record<string, PriceOverride> = {},
  now: Date = new Date(),
): CatalogRow[] {
  const nutritionMap = getNutritionMap(dataset.nutrition);
  const offersMap = getOffersByProduct(dataset.offers);

  return dataset.products.map((product) => {
    const nutrition = nutritionMap.get(product.id) ?? null;
    const offers = offersMap.get(product.id) ?? [];
    const price = selectPrice(
      product.id,
      offers,
      overrides[product.id],
      undefined,
      now,
    );
    const servingMl = nutrition?.servingMl ?? 0;
    const calories = nutrition?.calories ?? null;
    const serving = computeServingMetrics(
      servingMl,
      product.abvPercent,
      calories,
    );
    const pkg = computePackageMetrics(
      price.containerCount,
      price.containerMl || servingMl || 1,
      product.abvPercent,
      price.packagePriceUsd,
      servingMl || price.containerMl || 1,
    );

    let missingReason: string | null = null;
    if (product.abvPercent == null || product.abvPercent === 0) {
      missingReason =
        product.abvPercent === 0
          ? 'Zero-ABV items are not ranked on alcohol efficiency'
          : 'ABV unknown';
    } else if (calories == null && price.packagePriceUsd == null) {
      missingReason = 'Missing calories and price';
    } else if (calories == null) {
      missingReason = 'Missing calories — eligible for price sort only';
    } else if (price.packagePriceUsd == null) {
      missingReason = 'Missing price — eligible for calorie sort only';
    }

    return {
      product,
      nutrition,
      offers,
      serving,
      package: pkg,
      price,
      alcoholPer100Calories: serving.alcoholPer100Calories,
      alcoholPerDollar: pkg.alcoholPerDollar,
      caloriesPerStandardDrink: serving.caloriesPerStandardDrink,
      dollarsPerStandardDrink: pkg.dollarsPerStandardDrink,
      missingReason,
    };
  });
}

export function filterCatalog(
  rows: CatalogRow[],
  filters: CatalogFilters,
): CatalogRow[] {
  const q = filters.query.trim().toLowerCase();
  return rows.filter((row) => {
    const { product, nutrition, price } = row;

    if (filters.category !== 'all' && product.category !== filters.category) {
      return false;
    }
    if (filters.subtype && product.subtype !== filters.subtype) return false;

    // Unknown ABV stays visible (price-only rows). Only hide true 0% unless opted in.
    if (!filters.includeZeroAbv && product.abvPercent === 0) {
      return false;
    }

    if (filters.verifiedNutritionOnly) {
      if (!nutrition || nutrition.status !== 'verified') return false;
    }

    if (filters.pricedOnly && price.packagePriceUsd == null) return false;

    if (
      filters.priceProvenance !== 'all' &&
      price.provenance !== filters.priceProvenance
    ) {
      return false;
    }

    if (filters.abvMin != null && (product.abvPercent == null || product.abvPercent < filters.abvMin)) {
      return false;
    }
    if (filters.abvMax != null && (product.abvPercent == null || product.abvPercent > filters.abvMax)) {
      return false;
    }
    if (
      filters.caloriesMax != null &&
      (nutrition?.calories == null || nutrition.calories > filters.caloriesMax)
    ) {
      return false;
    }
    if (
      filters.carbsMax != null &&
      (nutrition?.carbsG == null || nutrition.carbsG > filters.carbsMax)
    ) {
      return false;
    }

    if (q) {
      const blob = searchBlob(
        product.name,
        product.brand,
        product.subtype,
        product.category,
        product.slug,
        ...product.aliases,
      );
      if (!matchesSearchBlob(blob, q)) return false;
    }

    return true;
  });
}

function compareNullable(
  a: number | null,
  b: number | null,
  direction: SortDirection,
): number {
  if (!isFiniteNumber(a) && !isFiniteNumber(b)) return 0;
  if (!isFiniteNumber(a)) return 1; // missing last
  if (!isFiniteNumber(b)) return -1;
  return direction === 'desc' ? b - a : a - b;
}

export function sortCatalog(
  rows: CatalogRow[],
  sortKey: SortKey,
  direction: SortDirection,
): CatalogRow[] {
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'alcoholPer100Calories':
        cmp = compareNullable(
          a.alcoholPer100Calories,
          b.alcoholPer100Calories,
          direction,
        );
        break;
      case 'alcoholPerDollar':
        cmp = compareNullable(a.alcoholPerDollar, b.alcoholPerDollar, direction);
        break;
      case 'abv':
        cmp = compareNullable(a.product.abvPercent, b.product.abvPercent, direction);
        break;
      case 'calories':
        cmp = compareNullable(
          a.nutrition?.calories ?? null,
          b.nutrition?.calories ?? null,
          direction,
        );
        break;
      case 'name':
        cmp =
          direction === 'asc'
            ? a.product.name.localeCompare(b.product.name)
            : b.product.name.localeCompare(a.product.name);
        break;
    }
    if (cmp !== 0) return cmp;
    return a.product.name.localeCompare(b.product.name);
  });
}

export function subtypesForCategory(
  rows: CatalogRow[],
  category: DrinkCategory | 'all',
): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (category !== 'all' && row.product.category !== category) continue;
    set.add(row.product.subtype);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export const CATEGORY_LABELS: Record<DrinkCategory | 'all', string> = {
  all: 'All drinks',
  beer: 'Beer',
  liquor: 'Liquor',
  wine: 'Wine',
  'mixed-drinks': 'Mixed drinks',
  seltzers: 'Seltzers & canned cocktails',
  cider: 'Cider',
};

export const CATEGORY_PATHS: Record<DrinkCategory, string> = {
  beer: '/beer/',
  liquor: '/liquor/',
  wine: '/wine/',
  'mixed-drinks': '/mixed-drinks/',
  seltzers: '/seltzers/',
  cider: '/cider/',
};
